import {
  FormControl,
  FormErrorMessage,
  Checkbox,
  CheckboxGroup,
  Stack,
  SimpleGrid,
} from "@chakra-ui/react";
import { FC, useEffect, useState } from "react";
import { useController, Control } from "react-hook-form";
import LabelComponent from "../../atom/Label/LabelComponent";
import { Option } from "../../../types/General/Option";
import { REQUIRED_FIELD } from "../../../utils/constants";
import SpinnerComponent from "../../atom/Spinner/SpinnerComponent";

interface CheckboxControlGroupProps {
  errors: any;
  name: string;
  control: Control<any>;
  data?: Option[];
  fieldName: string;
  fetchOptions?: () => Promise<Option[]>;
  isRequired?: boolean;
  isDisabled?: boolean;
  vertical?: boolean;
  quantity?: number;
}

const CheckboxControlGroup: FC<CheckboxControlGroupProps> = ({
  errors,
  name,
  control,
  data,
  fieldName,
  fetchOptions,
  isRequired = false,
  isDisabled = false,
  vertical = false,
  quantity = 4,
}) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const {
    field: { onChange, value },
  } = useController({
    name,
    control,
    defaultValue: [],
  });

  useEffect(() => {
    if (fetchOptions) {
      setIsLoaded(false);
      fetchOptions()
        .then((result) => {
          setOptions(result);
          setIsLoaded(true);
        })
        .catch((error) => {
          console.log("API Error:", error);
          setIsLoaded(true);
        });
    } else if (data) {
      setOptions(data);
      setIsLoaded(true);
    } else {
      setOptions([]);
      setIsLoaded(true);
    }
  }, [fetchOptions, data]);

  const handleChange = (selectedIds: string[]) => {
    const selectedOptions = options.filter((option) =>
      selectedIds.includes(option.id.toString()),
    );
    onChange(selectedOptions);
  };

  return (
    <FormControl isInvalid={errors && !!errors[name]}>
      <LabelComponent
        fontSize="md"
        fontWeight="500"
        title={fieldName}
        isRequired={isRequired}
      />
      {isLoaded ? (
        <CheckboxGroup
          onChange={handleChange}
          value={(value || []).map((v: Option) => v.id.toString())}
        >
          {vertical ? (
            <Stack direction="column">
              {options.map((option: Option) => (
                <Checkbox
                  key={option.id}
                  value={option.id.toString()}
                  isDisabled={isDisabled}
                >
                  {option.name}
                </Checkbox>
              ))}
            </Stack>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: quantity }} spacingY="4">
              {options.map((option: Option) => (
                <Checkbox
                  key={option.id}
                  value={option.id.toString()}
                  isDisabled={isDisabled}
                >
                  {option.name}
                </Checkbox>
              ))}
            </SimpleGrid>
          )}
        </CheckboxGroup>
      ) : (
        <SpinnerComponent size="lg" />
      )}
      <FormErrorMessage>
        {errors && errors[name] && <>{REQUIRED_FIELD}</>}
      </FormErrorMessage>
    </FormControl>
  );
};

export default CheckboxControlGroup;
